import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { MastraClient, RequestContext } from '@mastra/client-js';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';

import {
  injectTellentAssistantActorGetter,
  injectTellentAssistantAppContext,
  injectTellentAssistantAuthTokenGetter,
  injectTellentAssistantConfig,
  injectTellentAssistantResourceIdGetter,
} from '@core/tellent-assistant-contracts';
import { EMPTY_APP_CONTEXT, type ChatMessage } from '@core/tellent-assistant-types';
import {
  MastraStreamAccumulator,
  toChatMessages,
  toMastraUIMessages,
  toMastraUserMessage,
  type MastraInputMessage,
} from '@core/tellent-assistant-util';

import { setChatAppContext } from './chat-app-context';
import { AGENT_ID, buildTellentAssistantHeaders } from './tellent-assistant-headers';

const ERROR_BODY_PREVIEW_CHARS = 200;
const ABORT_ERROR_NAME = 'AbortError';

@Injectable({ providedIn: 'root' })
export class TellentAssistantMastraStreamClient {
  private readonly config = injectTellentAssistantConfig();
  private readonly getToken =
    injectTellentAssistantAuthTokenGetter({ optional: true }) ?? (() => null);
  private readonly getActor = injectTellentAssistantActorGetter({ optional: true }) ?? (() => null);
  private readonly getResourceId =
    injectTellentAssistantResourceIdGetter({ optional: true }) ?? (() => null);
  private readonly appContext = toSignal(
    injectTellentAssistantAppContext({ optional: true }) ?? of(EMPTY_APP_CONTEXT),
    { initialValue: EMPTY_APP_CONTEXT },
  );

  private readonly accumulator = new MastraStreamAccumulator();
  private readonly messagesSubject = new ReplaySubject<readonly ChatMessage[]>(1);
  private readonly runErrorSubject = new Subject<{ message: string }>();
  private readonly runFinalizedSubject = new Subject<void>();

  public readonly messages$: Observable<readonly ChatMessage[]> =
    this.messagesSubject.asObservable();
  public readonly runError$: Observable<{ message: string }> = this.runErrorSubject.asObservable();
  public readonly runFinalized$: Observable<void> = this.runFinalizedSubject.asObservable();

  private threadId: string | null = null;
  private threadCreatePromise: Promise<void> | null = null;
  private abortController: AbortController | null = null;

  public setThreadId(threadId: string): void {
    this.cancelRun();
    this.threadId = threadId;
    this.threadCreatePromise = null;
  }

  public markThreadAsNew(): void {
    const threadId = this.threadId;
    const resourceId = this.getResourceId();
    if (!threadId || !resourceId) return;
    this.threadCreatePromise = this.makeClient()
      .createMemoryThread({
        agentId: AGENT_ID,
        threadId,
        resourceId,
        metadata: { agentId: AGENT_ID },
      })
      .then(() => undefined);
  }

  public setMessages(msgs: readonly ChatMessage[]): void {
    this.cancelRun();
    this.accumulator.setMessages(toMastraUIMessages(msgs));
    this.emitMessages();
  }

  public addUserMessage(
    text: string,
    metadata?: Record<string, unknown>,
  ): ReturnType<typeof toMastraUserMessage> {
    const msg = toMastraUserMessage(text, metadata);
    this.accumulator.setMessages([...this.accumulator.messages, msg]);
    this.emitMessages();
    return msg;
  }

  public async run(runId: string, inputMessages: readonly MastraInputMessage[]): Promise<void> {
    const resourceId = this.getResourceId();
    const threadId = this.threadId;
    if (!threadId) {
      this.runErrorSubject.next({ message: 'No thread selected' });
      return;
    }
    this.abortController = new AbortController();
    this.accumulator.beginAssistantTurn();
    this.emitMessages();
    try {
      if (this.threadCreatePromise) {
        const create = this.threadCreatePromise;
        this.threadCreatePromise = null;
        await create;
      }
      const client = this.makeClient(this.abortController.signal);
      const agent = client.getAgent(AGENT_ID);
      const requestContext = new RequestContext();
      setChatAppContext(requestContext, this.appContext());
      const res = await agent.stream(<Parameters<typeof agent.stream>[0]>(<unknown>inputMessages), {
        runId,
        memory: { thread: { id: threadId }, resource: resourceId ?? '' },
        requestContext,
      });
      if (!res.ok) {
        const body = await res.text();
        this.runErrorSubject.next({
          message: `HTTP ${res.status}: ${body.slice(0, ERROR_BODY_PREVIEW_CHARS)}`,
        });
        return;
      }
      await res.processDataStream({
        onChunk: async c => {
          this.handleChunk(<{ type: string; payload?: Record<string, unknown> }>(<unknown>c));
        },
      });
    } catch (err) {
      if (err instanceof Error && err.name === ABORT_ERROR_NAME) return;
      this.runErrorSubject.next({
        message: err instanceof Error ? err.message : 'Stream failed',
      });
    } finally {
      this.accumulator.pruneTrailingEmptyAssistant();
      this.emitMessages();
      this.runFinalizedSubject.next();
      this.abortController = null;
    }
  }

  public cancelRun(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  private handleChunk(chunk: { type: string; payload?: Record<string, unknown> }): void {
    this.accumulator.apply(chunk);
    if (chunk.type === 'error') {
      const err = <{ error?: { message?: string } }>(chunk.payload ?? {});
      this.runErrorSubject.next({ message: err.error?.message ?? 'Stream error' });
    }
    this.emitMessages();
  }

  private emitMessages(): void {
    this.messagesSubject.next(toChatMessages(this.accumulator.messages));
  }

  private makeClient(abortSignal?: AbortSignal): MastraClient {
    return new MastraClient({
      baseUrl: this.config.apiUrl,
      headers: buildTellentAssistantHeaders(
        this.getToken(),
        this.getActor(),
        this.config.devPlayground,
      ),
      ...(abortSignal ? { abortSignal } : {}),
    });
  }
}
